/**
 * Creator & Project Management API Client
 *
 * Handles all API calls related to creator dashboard and project/collection management
 */

import { apiClient, ApiResponse } from './client';

// ==================== Types ====================

export type ProjectMemberRole = 'owner' | 'manager';

export interface ManagedProject {
  id: string;
  slug: string;
  name: string;
  image_url: string | null;
  description: string | null;
  collection_count: number;
  role: ProjectMemberRole;
}

export interface ProjectDashboard {
  project: {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    image_url: string | null;
    banner_url: string | null;
    website_url: string | null;
    twitter_url: string | null;
    discord_url: string | null;
    owner_address: string | null;
    enabled_features: string[] | null;
  };
  role: ProjectMemberRole;
  team: {
    count: number;
  };
  collections: Array<{
    id: string;
    slug: string | null;
    name: string;
    image_url: string | null;
    floor_price: number | null;
    total_supply: number;
    creator_address: string;
    taxon: number;
  }>;
}

export interface TeamMember {
  id: string;
  user_id: string;
  role: ProjectMemberRole;
  created_at: string;
  user: {
    username: string | null;
    xrpl_address: string;
    avatar_url: string | null;
  };
  invited_by?: {
    username: string | null;
    xrpl_address: string;
  };
}

export type AuditAction =
  | 'update_profile'
  | 'update_settings'
  | 'change_rarity'
  | 'change_points_strategy'
  | 'change_marketplace'
  | 'add_member'
  | 'remove_member'
  | 'transfer_ownership'
  | 'create_tag'
  | 'update_tag'
  | 'delete_tag'
  | 'bulk_update_tags'
  | 'set_points_override'
  | 'clear_points_override'
  | 'bulk_set_points'
  | 'send_announcement';

export interface AuditLogEntry {
  id: string;
  project_id: string;
  collection_id: string | null;
  user_id: string;
  action: AuditAction;
  details: Record<string, any>;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  username: string | null;
  xrpl_address: string;
  collection_name: string | null;
}

export interface ProjectCollection {
  id: string;
  slug: string | null;
  name: string;
  image_url: string | null;
  floor_price: number | null;
  total_supply: number;
  creator_address: string;
  taxon: number;
  nft_count: number;
}

// Collection management types (kept from original - used by CollectionManagePage)

export interface CollectionDashboard {
  collection: {
    id: string;
    name: string;
    slug: string | null;
    description: string | null;
    image_url: string | null;
    banner_url: string | null;
    creator_address: string;
    floor_price: number | null;
    total_volume: number | null;
    total_supply: number;
    website_url: string | null;
    external_url: string | null;
    rarity_methodology: string | null;
    preferred_marketplace: string | null;
    richlist_point_strategy: string | null;
  };
  project: {
    id: string;
    slug: string;
    name: string;
  } | null;
  stats: {
    floor_price: number | null;
    total_volume: number | null;
    total_supply: number;
    listed_count: number;
    unique_owners: number;
    volume_24h: number | null;
  };
  customTags: {
    count: number;
  };
  recentActivity: Array<any>;
  has_mutable_nfts?: boolean;
}

export interface CollectionSettings {
  rarity_methodology: string | null;
  richlist_point_strategy: string | null;
  preferred_marketplace: string | null;
  valuation_strategy?: string | null;
  skip_trait_stats?: boolean;
  has_points_system?: boolean;
  // Read-only fields
  royalty_percentage?: number | null;
  royalty_address?: string | null;
  creator_address?: string;
}

export interface CollectionOverview {
  recentSales: Array<{
    hash: string;
    amount: number;
    from_address: string;
    to_address: string;
    occurred_at: string;
    nft_name: string | null;
    nft_thumbnail_url: string | null;
    nft_token_id: string;
  }>;
  cheapestListings: Array<{
    token_id: string;
    name: string | null;
    thumbnail_url: string | null;
    min_sell_price: number;
    owner_address: string;
    rarity_rank: number | null;
  }>;
  holderDistribution: Array<{
    owner_address: string;
    nft_count: number;
  }>;
  recentMints: Array<{
    hash: string;
    to_address: string;
    occurred_at: string;
    nft_name: string | null;
    nft_thumbnail_url: string | null;
    nft_token_id: string;
  }>;
}

export type CustomTagType = 'text' | 'number' | 'url' | 'boolean';

export interface CustomTag {
  id: string;
  collection_id: string;
  name: string;
  display_name: string;
  tag_type: CustomTagType;
  description: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface NftCustomTag {
  id: string;
  nft_id: string;
  tag_id: string;
  value: string;
  tag_name: string;
  tag_display_name: string;
  tag_type: CustomTagType;
}

export interface PointOverride {
  nft_id: string;
  nft_token_id: string;
  nft_name: string | null;
  nft_image_url: string | null;
  collection_points: number;
  points_manual_override: boolean;
}

// ==================== Creator Dashboard API ====================

/**
 * Get all projects the user owns or manages
 */
export async function getMyProjects(): Promise<ManagedProject[]> {
  const response = await apiClient.get<ApiResponse<{
    projects: ManagedProject[];
  }>>('/creator/projects');

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch projects');
  }

  return response.data.projects;
}

export interface CreatorCollection {
  id: string;
  slug: string | null;
  name: string;
  image_url: string | null;
  floor_price: number | null;
  total_supply: number;
  creator_address: string;
  taxon: number;
  project_id: string | null;
  royalty_percentage: number;
}

/**
 * Get collections created by the authenticated user. By default returns only
 * "orphaned" collections (not linked to any project) — pass `{ includeProjectLinked: true }`
 * to get every collection they created, including ones attached to a project.
 */
export async function getMyCollections(
  options: { includeProjectLinked?: boolean } = {}
): Promise<CreatorCollection[]> {
  const path = options.includeProjectLinked
    ? '/creator/collections?orphaned=false'
    : '/creator/collections';
  const response = await apiClient.get<ApiResponse<{
    collections: CreatorCollection[];
  }>>(path);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch collections');
  }

  return response.data.collections;
}

export interface CreateProjectInput {
  name: string;
  slug?: string;
  description?: string | null;
  website_url?: string | null;
  twitter_url?: string | null;
  discord_url?: string | null;
  image_url?: string | null;
  banner_url?: string | null;
  tag_ids?: string[];
}

/**
 * Create a new project
 */
export async function createProject(data: CreateProjectInput): Promise<{ id: string; slug: string; name: string }> {
  const response = await apiClient.post<ApiResponse<{ id: string; slug: string; name: string }>>(
    '/creator/projects',
    data
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to create project');
  }

  return response.data;
}

/**
 * Get available project tags
 */
export async function getProjectTags(): Promise<Array<{ id: string; name: string; slug: string }>> {
  const response = await apiClient.get<ApiResponse<{ tags: Array<{ id: string; name: string; slug: string }> }>>(
    '/projects/tags'
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch project tags');
  }

  return response.data.tags;
}

/**
 * Assign a collection to a project (or remove from project with null)
 */
export async function updateCollectionProject(
  collectionId: string,
  projectId: string | null
): Promise<void> {
  const response = await apiClient.put<ApiResponse<{ updated: boolean }>>(
    `/creator/collections/${collectionId}/project`,
    { project_id: projectId }
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to update collection project');
  }
}

// ==================== Project Management API ====================

/**
 * Get project management dashboard
 */
export async function getProjectManageDashboard(slug: string): Promise<ProjectDashboard> {
  const response = await apiClient.get<ApiResponse<ProjectDashboard>>(
    `/creator/projects/${slug}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch project dashboard');
  }

  return response.data;
}

/**
 * Get project team members
 */
export async function getProjectTeam(slug: string): Promise<TeamMember[]> {
  const response = await apiClient.get<ApiResponse<{ members: TeamMember[]; count: number }>>(
    `/creator/projects/${slug}/team`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch team members');
  }

  return response.data.members;
}

/**
 * Add a manager to the project (owner only)
 */
export async function addProjectMember(
  slug: string,
  xrplAddress: string
): Promise<TeamMember> {
  const response = await apiClient.post<ApiResponse<{ member: TeamMember }>>(
    `/creator/projects/${slug}/team`,
    { xrpl_address: xrplAddress }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to add member');
  }

  return response.data.member;
}

/**
 * Remove a member from the project (owner only)
 */
export async function removeProjectMember(
  slug: string,
  userId: string
): Promise<void> {
  const response = await apiClient.delete<ApiResponse<void>>(
    `/creator/projects/${slug}/team/${userId}`
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to remove member');
  }
}

/**
 * Transfer project ownership (owner only)
 */
export async function transferProjectOwnership(
  slug: string,
  newOwnerUserId: string,
  keepManagers: boolean
): Promise<{ transferred: boolean }> {
  const response = await apiClient.post<ApiResponse<{ message: string; new_owner: any }>>(
    `/creator/projects/${slug}/transfer-ownership`,
    { new_owner_user_id: newOwnerUserId, keep_managers: keepManagers }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to transfer ownership');
  }

  return { transferred: true };
}

/**
 * Get project audit log
 */
export async function getProjectAuditLog(
  slug: string,
  limit = 50,
  offset = 0,
  action?: AuditAction,
  collectionId?: string
): Promise<{
  entries: AuditLogEntry[];
  total: number;
}> {
  const params = new URLSearchParams({
    page: String(Math.floor(offset / limit) + 1),
    limit: limit.toString(),
  });
  if (action) params.set('action', action);
  if (collectionId) params.set('collectionId', collectionId);

  const response = await apiClient.get<ApiResponse<{
    logs: AuditLogEntry[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }>>(`/creator/projects/${slug}/audit-log?${params.toString()}`);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch audit log');
  }

  return {
    entries: response.data.logs,
    total: response.data.pagination.total,
  };
}

/**
 * Get project collections with stats
 */
export async function getProjectCollections(slug: string): Promise<ProjectCollection[]> {
  const response = await apiClient.get<ApiResponse<{
    collections: ProjectCollection[];
    count: number;
  }>>(`/creator/projects/${slug}/collections`);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch collections');
  }

  return response.data.collections;
}

// ==================== Collection Management API ====================

const collectionManageBase = (projectSlug: string | null, collectionSlug: string) =>
  projectSlug
    ? `/creator/projects/${projectSlug}/collections/${collectionSlug}/manage`
    : `/creator/collections/${collectionSlug}/manage`;

/**
 * Get collection management dashboard (under project context)
 */
export async function getCollectionDashboard(
  projectSlug: string | null,
  collectionSlug: string
): Promise<CollectionDashboard> {
  const response = await apiClient.get<ApiResponse<CollectionDashboard>>(
    collectionManageBase(projectSlug, collectionSlug)
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch collection dashboard');
  }

  return response.data;
}

/**
 * Get collection overview data (recent sales, cheapest listings, holder distribution)
 */
export async function getCollectionOverview(
  projectSlug: string | null,
  collectionSlug: string
): Promise<CollectionOverview> {
  const response = await apiClient.get<ApiResponse<CollectionOverview>>(
    `${collectionManageBase(projectSlug, collectionSlug)}/overview`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch collection overview');
  }

  return response.data;
}

/**
 * Export mint data for CSV download
 */
export async function exportCollectionMints(
  projectSlug: string | null,
  collectionSlug: string,
  days: 1 | 3 | 7 | 30
): Promise<{
  mints: Array<{
    hash: string;
    to_address: string;
    occurred_at: string;
    nft_name: string | null;
    nft_token_id: string;
  }>;
  days: number;
}> {
  const response = await apiClient.get<ApiResponse<{
    mints: Array<{
      hash: string;
      to_address: string;
      occurred_at: string;
      nft_name: string | null;
      nft_token_id: string;
    }>;
    days: number;
  }>>(`${collectionManageBase(projectSlug, collectionSlug)}/mints/export?days=${days}`);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to export mints');
  }

  return response.data;
}

/**
 * Update collection profile
 */
export async function updateCollectionProfile(
  projectSlug: string | null,
  collectionSlug: string,
  data: {
    name?: string;
    description?: string;
    image_url?: string;
    banner_url?: string;
    website_url?: string;
  }
): Promise<{ updated: boolean }> {
  const response = await apiClient.put<ApiResponse<{ updated: boolean }>>(
    `${collectionManageBase(projectSlug, collectionSlug)}/profile`,
    data
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to update collection profile');
  }

  return response.data;
}

/**
 * Upload collection logo image
 */
export async function uploadCollectionLogo(
  projectSlug: string | null,
  collectionSlug: string,
  file: File
): Promise<{ image_url: string }> {
  const formData = new FormData();
  formData.append('logo', file);

  const url = `${(apiClient as any).baseUrl}${collectionManageBase(projectSlug, collectionSlug)}/profile/logo`;
  const token = localStorage.getItem('xrpl_marketplace_token');

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to upload collection logo');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Get collection settings
 */
export async function getCollectionSettings(
  projectSlug: string | null,
  collectionSlug: string
): Promise<CollectionSettings> {
  const response = await apiClient.get<ApiResponse<CollectionSettings>>(
    `${collectionManageBase(projectSlug, collectionSlug)}/settings`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch collection settings');
  }

  return response.data;
}

/**
 * Update collection settings
 */
export async function updateCollectionSettings(
  projectSlug: string | null,
  collectionSlug: string,
  data: Partial<CollectionSettings>
): Promise<{ settings: Record<string, any>; changes: Record<string, { old: any; new: any }> }> {
  const response = await apiClient.put<ApiResponse<{ settings: Record<string, any>; changes: Record<string, { old: any; new: any }> }>>(
    `${collectionManageBase(projectSlug, collectionSlug)}/settings`,
    data
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to update collection settings');
  }

  return response.data;
}

// ==================== Custom Tags API (under project context) ====================

/**
 * Get all custom tags for a collection
 */
export async function getCustomTags(
  projectSlug: string | null,
  collectionSlug: string
): Promise<CustomTag[]> {
  const response = await apiClient.get<ApiResponse<{ tags: CustomTag[] }>>(
    `${collectionManageBase(projectSlug, collectionSlug)}/tags`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch custom tags');
  }

  return response.data.tags;
}

/**
 * Create a custom tag
 */
export async function createCustomTag(
  projectSlug: string | null,
  collectionSlug: string,
  data: {
    name: string;
    display_name: string;
    tag_type: CustomTagType;
    description?: string;
  }
): Promise<CustomTag> {
  const response = await apiClient.post<ApiResponse<{ tag: CustomTag }>>(
    `${collectionManageBase(projectSlug, collectionSlug)}/tags`,
    data
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to create custom tag');
  }

  return response.data.tag;
}

/**
 * Update a custom tag
 */
export async function updateCustomTag(
  projectSlug: string | null,
  collectionSlug: string,
  tagId: string,
  data: {
    display_name?: string;
    description?: string;
    display_order?: number;
  }
): Promise<CustomTag> {
  const response = await apiClient.put<ApiResponse<{ tag: CustomTag }>>(
    `${collectionManageBase(projectSlug, collectionSlug)}/tags/${tagId}`,
    data
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to update custom tag');
  }

  return response.data.tag;
}

/**
 * Delete a custom tag
 */
export async function deleteCustomTag(
  projectSlug: string | null,
  collectionSlug: string,
  tagId: string
): Promise<void> {
  const response = await apiClient.delete<ApiResponse<void>>(
    `${collectionManageBase(projectSlug, collectionSlug)}/tags/${tagId}`
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to delete custom tag');
  }
}

/**
 * Set tag values for an NFT (sets multiple tags at once)
 */
export async function setNftTagValues(
  projectSlug: string | null,
  collectionSlug: string,
  nftTokenId: string,
  tags: Array<{ tag_id: string; value: string }>
): Promise<{ updated: number; tags: NftCustomTag[] }> {
  const response = await apiClient.put<ApiResponse<{ updated: number; tags: NftCustomTag[] }>>(
    `${collectionManageBase(projectSlug, collectionSlug)}/tags/nfts/${nftTokenId}`,
    { tags }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to set tag values');
  }

  return response.data;
}

/**
 * Bulk update NFT tag values
 */
export async function bulkUpdateTags(
  projectSlug: string | null,
  collectionSlug: string,
  tagId: string,
  updates: Array<{ nft_token_id: string; value: string }>
): Promise<{ updated: number; not_found: string[] }> {
  const response = await apiClient.post<ApiResponse<{
    updated: number;
    not_found: string[];
  }>>(`${collectionManageBase(projectSlug, collectionSlug)}/tags/bulk-update`, { tag_id: tagId, updates });

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to bulk update tags');
  }

  return response.data;
}

/**
 * Export NFTs with custom tag values (for CSV download)
 */
export async function exportCustomTags(
  projectSlug: string | null,
  collectionSlug: string
): Promise<{
  tags: Array<{ name: string; display_name: string }>;
  nfts: Array<{ nft_token_id: string; name: string; tags: Record<string, string> }>;
}> {
  const response = await apiClient.get<ApiResponse<{
    tags: Array<{ name: string; display_name: string }>;
    nfts: Array<{ nft_token_id: string; name: string; tags: Record<string, string> }>;
  }>>(`${collectionManageBase(projectSlug, collectionSlug)}/tags/export`);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to export custom tags');
  }

  return response.data;
}

// ==================== Points Management API (under project context) ====================

/**
 * Get NFTs with manual point overrides
 */
export async function getPointOverrides(
  projectSlug: string | null,
  collectionSlug: string,
  limit = 50,
  offset = 0
): Promise<{
  overrides: PointOverride[];
  total: number;
}> {
  const response = await apiClient.get<ApiResponse<{
    nfts: Array<{
      id: string;
      token_id: string;
      name: string | null;
      image_url: string | null;
      collection_points: number;
      points_manual_override: boolean;
    }>;
    pagination: { page: number; limit: number; total: number; pages: number };
  }>>(`${collectionManageBase(projectSlug, collectionSlug)}/points/overrides?limit=${limit}&page=${Math.floor(offset / limit) + 1}`);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch point overrides');
  }

  return {
    overrides: response.data.nfts.map(nft => ({
      nft_id: nft.id,
      nft_token_id: nft.token_id,
      nft_name: nft.name,
      nft_image_url: nft.image_url,
      collection_points: nft.collection_points,
      points_manual_override: nft.points_manual_override,
    })),
    total: response.data.pagination.total,
  };
}

/**
 * Set manual point override for an NFT
 */
export async function setPointOverride(
  projectSlug: string | null,
  collectionSlug: string,
  nftTokenId: string,
  points: number
): Promise<{ nft_token_id: string; points: number; manual_override: boolean }> {
  const response = await apiClient.put<ApiResponse<{ nft_token_id: string; points: number; manual_override: boolean }>>(
    `${collectionManageBase(projectSlug, collectionSlug)}/points/nfts/${nftTokenId}`,
    { points }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to set point override');
  }

  return response.data;
}

/**
 * Clear manual point override (revert to calculated)
 */
export async function clearPointOverride(
  projectSlug: string | null,
  collectionSlug: string,
  nftTokenId: string
): Promise<void> {
  const response = await apiClient.delete<ApiResponse<void>>(
    `${collectionManageBase(projectSlug, collectionSlug)}/points/nfts/${nftTokenId}`
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to clear point override');
  }
}

/**
 * Bulk update point overrides
 */
export async function bulkUpdatePoints(
  projectSlug: string | null,
  collectionSlug: string,
  updates: Array<{ nft_token_id: string; points: number }>
): Promise<{ updated: number; not_found: string[] }> {
  const response = await apiClient.post<ApiResponse<{
    updated: number;
    not_found: string[];
  }>>(`${collectionManageBase(projectSlug, collectionSlug)}/points/bulk-update`, { updates });

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to bulk update points');
  }

  return response.data;
}
